/**
 * External location search provider factory（MAP-GOOGLE-E1 / E2）。
 *
 * mode 'disabled'：search() 永遠回傳 []，enabled=false；不載 Google script。
 * mode 'fake'：query.length >= 2 時回傳固定候選（測試）。
 * mode 'google'：Places Autocomplete Data API（New）；lazy load + session token。
 *
 * Browser key 與 server Place Details key 分離；預設 production disabled。
 */

import { loadGooglePlacesLibrary } from './googlePlacesScriptLoader';

export const EXTERNAL_SEARCH_MIN_QUERY_LENGTH = 2;
export const EXTERNAL_SEARCH_DEBOUNCE_MS = 300;

/** 搜尋邊界：trim 後再評估長度與送 Google（E3D whitespace debt）。 */
export function normalizeExternalSearchQuery(query) {
  if (query == null) return '';
  return String(query).trim();
}

const FAKE_CANDIDATES = [
  {
    provider: 'GOOGLE_PLACES',
    providerPlaceId: 'ChIJfake_taipei_101',
    primaryText: 'Taipei 101',
    secondaryText: 'No.7 Section 5, Xinyi Road, Xinyi District, Taipei City, Taiwan',
  },
  {
    provider: 'GOOGLE_PLACES',
    providerPlaceId: 'ChIJfake_sun_moon_lake',
    primaryText: 'Sun Moon Lake',
    secondaryText: 'Yuchi Township, Nantou County, Taiwan',
  },
];

/**
 * 由 CRA env 決定 runtime mode（預設 disabled；缺 key 亦 disabled）。
 * @param {NodeJS.ProcessEnv | Record<string, string | undefined>} [env]
 * @returns {'disabled' | 'google'}
 */
export function resolveExternalSearchModeFromEnv(env = process.env) {
  const enabled = String(env.REACT_APP_GOOGLE_PLACES_BROWSER_ENABLED || '').toLowerCase() === 'true';
  const key = String(env.REACT_APP_GOOGLE_PLACES_BROWSER_KEY || '').trim();
  if (enabled && key) return 'google';
  return 'disabled';
}

export function readGooglePlacesBrowserKey(env = process.env) {
  return String(env.REACT_APP_GOOGLE_PLACES_BROWSER_KEY || '').trim();
}

/**
 * 建立外部地點搜尋 provider。
 * @param {{
 *   mode?: 'disabled' | 'fake' | 'google',
 *   browserApiKey?: string,
 *   loadPlacesLibrary?: (key: string) => Promise<any>,
 *   minQueryLength?: number,
 * }} [options]
 */
export function createExternalLocationSearchProvider({
  mode = 'disabled',
  browserApiKey = '',
  loadPlacesLibrary = loadGooglePlacesLibrary,
  minQueryLength = EXTERNAL_SEARCH_MIN_QUERY_LENGTH,
} = {}) {
  if (mode === 'fake') {
    return {
      enabled: true,
      mode: 'fake',
      search: async (query, { signal } = {}) => {
        if (signal?.aborted) throw abortError();
        const normalized = normalizeExternalSearchQuery(query);
        if (!normalized || normalized.length < minQueryLength) return [];
        const lower = normalized.toLowerCase();
        return FAKE_CANDIDATES.filter(
          (c) =>
            c.primaryText.toLowerCase().includes(lower) ||
            c.secondaryText.toLowerCase().includes(lower),
        );
      },
    };
  }

  if (mode === 'google') {
    const key = String(browserApiKey || '').trim();
    if (!key) {
      // 缺 key：視為 disabled，避免載入 script / 發 request
      return disabledProvider();
    }
    return createGooglePlacesSearchProvider({
      browserApiKey: key,
      loadPlacesLibrary,
      minQueryLength,
    });
  }

  return disabledProvider();
}

function disabledProvider() {
  return {
    enabled: false,
    mode: 'disabled',
    search: async () => [],
  };
}

function createGooglePlacesSearchProvider({ browserApiKey, loadPlacesLibrary, minQueryLength }) {
  let placesApiPromise = null;
  let sessionToken = null;
  let AutocompleteSessionTokenCtor = null;
  let AutocompleteSuggestionApi = null;

  async function ensureApi() {
    if (AutocompleteSuggestionApi && AutocompleteSessionTokenCtor) {
      return;
    }
    if (!placesApiPromise) {
      placesApiPromise = loadPlacesLibrary(browserApiKey).then((places) => {
        AutocompleteSessionTokenCtor = places.AutocompleteSessionToken;
        AutocompleteSuggestionApi = places.AutocompleteSuggestion;
        if (!AutocompleteSessionTokenCtor || !AutocompleteSuggestionApi?.fetchAutocompleteSuggestions) {
          throw new Error('Google Places Autocomplete API unavailable');
        }
      }).catch((err) => {
        placesApiPromise = null;
        throw err;
      });
    }
    await placesApiPromise;
  }

  function ensureSessionToken() {
    if (!sessionToken) {
      sessionToken = new AutocompleteSessionTokenCtor();
    }
    return sessionToken;
  }

  function retireSessionToken() {
    sessionToken = null;
  }

  return {
    enabled: true,
    mode: 'google',
    /** 元件 mount 時預先 await library readiness，避免首次 query 撞上 cold init。 */
    warmup: () => ensureApi(),
    /** 測試／診斷：目前 session token 是否存在 */
    hasActiveSessionToken: () => sessionToken != null,
    retireSessionToken,
    search: async (query, { signal } = {}) => {
      if (signal?.aborted) throw abortError();
      const normalized = normalizeExternalSearchQuery(query);
      if (!normalized || normalized.length < minQueryLength) return [];

      await ensureApi();
      if (signal?.aborted) throw abortError();

      const token = ensureSessionToken();
      const { suggestions } = await AutocompleteSuggestionApi.fetchAutocompleteSuggestions({
        input: normalized,
        sessionToken: token,
      });
      if (signal?.aborted) throw abortError();

      return (suggestions || [])
        .map((suggestion) => mapSuggestion(suggestion))
        .filter(Boolean);
    },
  };
}

function mapSuggestion(suggestion) {
  const prediction = suggestion?.placePrediction;
  if (!prediction) return null;
  const placeId = prediction.placeId;
  if (!placeId) return null;
  const primary =
    stringifyPredictionText(prediction.mainText)
    || stringifyPredictionText(prediction.text)
    || placeId;
  const secondary = stringifyPredictionText(prediction.secondaryText) || '';
  return {
    provider: 'GOOGLE_PLACES',
    providerPlaceId: placeId,
    primaryText: primary,
    secondaryText: secondary,
  };
}

function stringifyPredictionText(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value.toString === 'function') {
    const text = value.toString();
    return text === '[object Object]' ? '' : text;
  }
  return '';
}

function abortError() {
  try {
    return new DOMException('Aborted', 'AbortError');
  } catch {
    const err = new Error('Aborted');
    err.name = 'AbortError';
    return err;
  }
}
